import { Option, Result, Schema } from "effect";
import { schemaParser, schemaDecoder } from "./schema-compat.ts";
import { IdentityObjectInput } from "./identity-input.ts";

import { AgenticTraceContextInput } from "./agentic-correlation.ts";

/**
 * Pure shared model for flow recordings: identifiers, R2 key layout, the
 * `.fpack` frame container, rrweb JSONL segments, the in-page chunk protocol,
 * the capture backpressure state machine, marker normalization, and clock
 * calibration. This module must stay importable from Node test heads, SQLite
 * Durable Objects, and injected browser runtimes alike — no Workers bindings,
 * no DOM access, no side effects.
 */

export const FLOW_RECORDING_MODES = ["video", "dom", "both"] as const;
const NonNegativeSafeInteger = /* @__PURE__ */ (() => Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)))();
const PositiveSafeInteger = /* @__PURE__ */ (() => Schema.Int.check(Schema.isGreaterThanOrEqualTo(1)))();
const PositiveFinite = /* @__PURE__ */ (() => Schema.Finite.check(Schema.isGreaterThan(0)))();
const isString = /* @__PURE__ */ (() => Schema.is(Schema.String))();

export const FlowRecordingMode = /* @__PURE__ */ (() => Schema.Literals(FLOW_RECORDING_MODES))();
export type FlowRecordingMode = typeof FlowRecordingMode.Type;

export const FLOW_RECORDING_STATES = [
  "recording",
  "complete",
  "interrupted",
  "expired"
] as const;
export const FlowRecordingState = /* @__PURE__ */ (() => Schema.Literals(FLOW_RECORDING_STATES))();
export type FlowRecordingState = typeof FlowRecordingState.Type;

export const FLOW_RECORDING_MARKER_KINDS = [
  "step",
  "assertion",
  "error",
  "navigation",
  "test-start",
  "test-pass",
  "test-fail",
  "dropped-frames",
  "quality-degraded",
  "truncated",
  "custom"
] as const;
export const FlowRecordingMarkerKind = /* @__PURE__ */ (() => Schema.Literals(FLOW_RECORDING_MARKER_KINDS))();
export type FlowRecordingMarkerKind = typeof FlowRecordingMarkerKind.Type;

export const isFlowRecordingMode = /* @__PURE__ */ (() => Schema.is(FlowRecordingMode))();
export const isFlowRecordingState = /* @__PURE__ */ (() => Schema.is(FlowRecordingState))();
export const isFlowRecordingMarkerKind = /* @__PURE__ */ (() => Schema.is(FlowRecordingMarkerKind))();

export const FLOW_RECORDING_SEGMENT_KINDS = ["video", "dom"] as const;
export const FlowRecordingSegmentKind = /* @__PURE__ */ (() => Schema.Literals(FLOW_RECORDING_SEGMENT_KINDS))();
export type FlowRecordingSegmentKind = typeof FlowRecordingSegmentKind.Type;

export const isFlowRecordingSegmentKind = /* @__PURE__ */ (() => Schema.is(FlowRecordingSegmentKind))();

export const FlowRecordingScreencastPolicy = /* @__PURE__ */ (() => Schema.Struct({
  format: Schema.Literal("jpeg").pipe(Schema.mutableKey),
  quality: Schema.Number.pipe(Schema.mutableKey),
  maxWidth: Schema.Number.pipe(Schema.mutableKey),
  maxHeight: Schema.Number.pipe(Schema.mutableKey),
  everyNthFrame: Schema.Number.pipe(Schema.mutableKey),
}))();
export type FlowRecordingScreencastPolicy = typeof FlowRecordingScreencastPolicy.Type;

/** Capture policy actually applied to a recording, persisted on its row. */
export const FlowRecordingAppliedPolicy = /* @__PURE__ */ (() => Schema.Struct({
  maskAllInputs: Schema.Boolean.pipe(Schema.mutableKey),
  /** Password inputs are always masked; recorded for auditability. */
  maskPasswords: Schema.Boolean.pipe(Schema.mutableKey),
  blockClass: Schema.String.pipe(Schema.mutableKey),
  recordCanvas: Schema.Boolean.pipe(Schema.mutableKey),
  checkoutEveryNms: Schema.Number.pipe(Schema.mutableKey),
  screencast: FlowRecordingScreencastPolicy.pipe(Schema.mutableKey),
  retentionMs: Schema.Number.pipe(Schema.mutableKey),
  maxDurationMs: Schema.Number.pipe(Schema.mutableKey),
  maxTotalBytes: Schema.Number.pipe(Schema.mutableKey),
}))();
export type FlowRecordingAppliedPolicy = typeof FlowRecordingAppliedPolicy.Type;

/**
 * A recording index row. Extends the agentic trace context so every recording
 * carries the same 18 correlation identities as browser observations.
 */
export const FlowRecording = /* @__PURE__ */ (() => Schema.Struct({
  ...AgenticTraceContextInput.fields,
  recordingId: Schema.String.pipe(Schema.mutableKey),
  workspaceId: Schema.String.pipe(Schema.mutableKey),
  stationId: Schema.String.pipe(Schema.optional, Schema.mutableKey),
  flowName: Schema.String.pipe(Schema.mutableKey),
  mode: FlowRecordingMode.pipe(Schema.mutableKey),
  state: FlowRecordingState.pipe(Schema.mutableKey),
  /** Browser Rendering session id, when the recorded session has one. */
  sessionId: Schema.String.pipe(Schema.optional, Schema.mutableKey),
  startedAt: Schema.Number.pipe(Schema.mutableKey),
  endedAt: Schema.Number.pipe(Schema.optional, Schema.mutableKey),
  lastHeartbeatAt: Schema.Number.pipe(Schema.optional, Schema.mutableKey),
  expiresAt: Schema.Number.pipe(Schema.mutableKey),
  totalBytes: Schema.Number.pipe(Schema.mutableKey),
  segmentCount: Schema.Number.pipe(Schema.mutableKey),
  markerCount: Schema.Number.pipe(Schema.mutableKey),
  appliedPolicy: FlowRecordingAppliedPolicy.pipe(Schema.mutableKey),
  /** `FLOW_RECORDING_RUNTIME_VERSION` of the injected recorder, when dom mode ran. */
  runtimeVersion: Schema.Number.pipe(Schema.mutableKey),
  source: Schema.Literals(["self-capture", "native-import"]).pipe(Schema.optional, Schema.mutableKey),
}))();
export type FlowRecording = typeof FlowRecording.Type;

export const FlowRecordingSegment = /* @__PURE__ */ (() => Schema.Struct({
  recordingId: Schema.String.pipe(Schema.mutableKey),
  seq: Schema.Number.pipe(Schema.mutableKey),
  kind: FlowRecordingSegmentKind.pipe(Schema.mutableKey),
  /** R2 object key under the reserved `flow-recording:v1:` prefix. */
  key: Schema.String.pipe(Schema.mutableKey),
  startMs: Schema.Number.pipe(Schema.mutableKey),
  endMs: Schema.Number.pipe(Schema.mutableKey),
  byteLength: Schema.Number.pipe(Schema.mutableKey),
  frameCount: Schema.Number.pipe(Schema.optional, Schema.mutableKey),
  eventCount: Schema.Number.pipe(Schema.optional, Schema.mutableKey),
}))();
export type FlowRecordingSegment = typeof FlowRecordingSegment.Type;

export const FlowRecordingMarker = /* @__PURE__ */ (() => Schema.Struct({
  kind: FlowRecordingMarkerKind.pipe(Schema.mutableKey),
  label: Schema.String.pipe(Schema.mutableKey),
  timestampMs: Schema.Number.pipe(Schema.mutableKey),
  detail: Schema.Unknown.pipe(Schema.optional, Schema.mutableKey),
}))();
export type FlowRecordingMarker = typeof FlowRecordingMarker.Type;

export const FlowRecordingManifest = /* @__PURE__ */ (() => Schema.Struct({
  recording: FlowRecording.pipe(Schema.mutableKey),
  segments: Schema.Array(FlowRecordingSegment).pipe(Schema.mutable, Schema.mutableKey),
  markers: Schema.Array(FlowRecordingMarker).pipe(Schema.mutable, Schema.mutableKey),
  generatedAt: Schema.Number.pipe(Schema.mutableKey),
}))();
export type FlowRecordingManifest = typeof FlowRecordingManifest.Type;

// ---------------------------------------------------------------------------
// Identifiers
// ---------------------------------------------------------------------------

export const FLOW_RECORDING_ID_RE = /^[0-9a-z]{1,13}-[0-9a-z]{4,16}$/;
export const FLOW_RECORDING_WORKSPACE_RE = /^[a-z0-9][a-z0-9-]{0,63}$/;
export const FLOW_RECORDING_MAX_SEGMENT_SEQ = 999_999;

/** `<epochMs base36>-<random base36>`: sortable by start time, unguessable tail. */
export function generateFlowRecordingId(nowMs: number, words: Uint32Array): string {
  const epoch = Math.max(0, Math.floor(nowMs)).toString(36);
  const random = Array.from(words, (word) =>
    word.toString(36).padStart(7, "0").slice(-7)
  )
    .join("")
    .slice(0, 12);
  const id = `${epoch}-${random}`;
  return schemaParser(FlowRecordingId,
    () => new RangeError("Generated flow recording id is out of range."))(id);
}

export const FlowRecordingId = /* @__PURE__ */ (() => Schema.String.check(Schema.isPattern(FLOW_RECORDING_ID_RE)))();
export const FlowRecordingWorkspaceId = /* @__PURE__ */ (() => Schema.String.check(Schema.isPattern(FLOW_RECORDING_WORKSPACE_RE)))();
export const FlowRecordingSegmentSeq = /* @__PURE__ */ (() => Schema.Int.check(Schema.isBetween({ minimum: 0, maximum: FLOW_RECORDING_MAX_SEGMENT_SEQ })))();
export const isFlowRecordingId = /* @__PURE__ */ (() => Schema.is(FlowRecordingId))();
export const isFlowRecordingWorkspaceId = /* @__PURE__ */ (() => Schema.is(FlowRecordingWorkspaceId))();
export const isFlowRecordingSegmentSeq = /* @__PURE__ */ (() => Schema.is(FlowRecordingSegmentSeq))();

// ---------------------------------------------------------------------------
// Reserved R2 key layout
// ---------------------------------------------------------------------------

export const FLOW_RECORDING_R2_PREFIX = "flow-recording:v1:";
/** The hosted scoped-R2 key space; recording keys must never address it. */
export const HOSTED_NESTED_R2_PREFIX = "nested-r2:v1:";

/**
 * Assert a key lives inside the reserved recording prefix and cannot collide
 * with the hosted `nested-r2:v1:` key space or escape via path tricks.
 */
const ReservedPrefixKey = /* @__PURE__ */ (() => schemaDecoder(Schema.String.check(Schema.isStartsWith(FLOW_RECORDING_R2_PREFIX)), () => new TypeError(`Flow recording keys must start with "${FLOW_RECORDING_R2_PREFIX}".`)))();
export const ReservedFlowRecordingKey = /* @__PURE__ */ (() => Schema.String.check(
  Schema.isStartsWith(FLOW_RECORDING_R2_PREFIX),
  Schema.makeFilter(key => {
    const suffix = key.slice(FLOW_RECORDING_R2_PREFIX.length);
    return !suffix.includes(":") && !suffix.includes("//") &&
      !suffix.split("/").some(part => part === "." || part === "..");
  })
))();
const recordingKey = /* @__PURE__ */ (() => schemaDecoder(ReservedFlowRecordingKey, () => new TypeError("Flow recording key contains unsafe path segments.")))();
// The legacy facade preserves its two public diagnostics; both checks are
// derived from the reserved-key Schema rather than a parallel key grammar.
export const assertReservedFlowRecordingKey = (key: string): string =>
  recordingKey.parse(ReservedPrefixKey.parse(key));

const requireWorkspaceId = /* @__PURE__ */ (() => schemaParser(FlowRecordingWorkspaceId, () => new TypeError("Flow recording workspace id must be a lowercase resource slug.")))();
const requireRecordingId = /* @__PURE__ */ (() => schemaParser(FlowRecordingId, () => new TypeError("Flow recording id must match <epochMs base36>-<random base36>.")))();
const recordingSeq = /* @__PURE__ */ (() => schemaDecoder(FlowRecordingSegmentSeq, () => new RangeError(`Flow recording segment seq must be an integer between 0 and ${FLOW_RECORDING_MAX_SEGMENT_SEQ}.`)))();
function segmentSeqLabel(seq: number): string {
  return String(recordingSeq.parse(seq)).padStart(6, "0");
}

export function flowRecordingObjectPrefix(
  workspaceId: string,
  recordingId: string
): string {
  const prefix = `${FLOW_RECORDING_R2_PREFIX}${requireWorkspaceId(workspaceId)}/${requireRecordingId(recordingId)}/`;
  assertReservedFlowRecordingKey(prefix);
  return prefix;
}

export function flowRecordingVideoSegmentKey(
  workspaceId: string,
  recordingId: string,
  seq: number
): string {
  return assertReservedFlowRecordingKey(
    `${flowRecordingObjectPrefix(workspaceId, recordingId)}video/segment-${segmentSeqLabel(seq)}.fpack`
  );
}

export function flowRecordingDomSegmentKey(
  workspaceId: string,
  recordingId: string,
  seq: number
): string {
  return assertReservedFlowRecordingKey(
    `${flowRecordingObjectPrefix(workspaceId, recordingId)}rrweb/segment-${segmentSeqLabel(seq)}.jsonl.gz`
  );
}

export function flowRecordingManifestKey(
  workspaceId: string,
  recordingId: string
): string {
  return assertReservedFlowRecordingKey(
    `${flowRecordingObjectPrefix(workspaceId, recordingId)}manifest.json`
  );
}

export function flowRecordingExportKey(
  workspaceId: string,
  recordingId: string
): string {
  return assertReservedFlowRecordingKey(
    `${flowRecordingObjectPrefix(workspaceId, recordingId)}export/video.mp4`
  );
}

// ---------------------------------------------------------------------------
// .fpack frame container
// ---------------------------------------------------------------------------

export const FPACK_MAGIC = "FPK1";
const FPACK_MAGIC_BYTES = [0x46, 0x50, 0x4b, 0x31] as const;
/** u32 jpeg length + f64 timestampMs + u16 width + u16 height, little-endian. */
export const FPACK_FRAME_HEADER_BYTES = 16;

const FpackDimension = /* @__PURE__ */ (() => Schema.Int.check(Schema.isBetween({ minimum: 0, maximum: 0xffff })))();
const fpackJpeg = /* @__PURE__ */ (() => schemaParser(Schema.instanceOf(Uint8Array), () => new TypeError("fpack frames must carry Uint8Array jpeg bytes.")))();
const fpackTimestamp = /* @__PURE__ */ (() => schemaParser(Schema.Finite, () => new RangeError("fpack frame timestamps must be finite numbers.")))();

export const FpackFrameInput = /* @__PURE__ */ (() => Schema.Struct({
  timestampMs: Schema.Finite.pipe(Schema.mutableKey),
  jpeg: Schema.Uint8Array.pipe(Schema.mutableKey),
  width: FpackDimension.pipe(Schema.optional, Schema.mutableKey),
  height: FpackDimension.pipe(Schema.optional, Schema.mutableKey),
}))();
export type FpackFrameInput = typeof FpackFrameInput.Type;

export const FpackFrame = /* @__PURE__ */ (() => Schema.Struct({
  timestampMs: Schema.Number.pipe(Schema.mutableKey),
  jpeg: Schema.Uint8Array.pipe(Schema.mutableKey),
  width: FpackDimension.pipe(Schema.optional, Schema.mutableKey),
  height: FpackDimension.pipe(Schema.optional, Schema.mutableKey),
}))();
export type FpackFrame = typeof FpackFrame.Type;

export const FpackFrameIndexEntry = /* @__PURE__ */ (() => Schema.Struct({
  timestampMs: Schema.Number.pipe(Schema.mutableKey),
  /** Offset of the raw JPEG bytes within the segment, for ranged reads. */
  byteOffset: Schema.Number.pipe(Schema.mutableKey),
  byteLength: Schema.Number.pipe(Schema.mutableKey),
}))();
export type FpackFrameIndexEntry = typeof FpackFrameIndexEntry.Type;

export const FpackEncodeResult = /* @__PURE__ */ (() => Schema.Struct({
  bytes: Schema.Uint8Array.pipe(Schema.mutableKey),
  frameIndex: Schema.Array(FpackFrameIndexEntry).pipe(Schema.mutable, Schema.mutableKey),
}))();
export type FpackEncodeResult = typeof FpackEncodeResult.Type;

export const FpackDecodeOptions = /* @__PURE__ */ (() => Schema.Struct({
  /** Decode every complete frame before a truncation cut instead of throwing. */
  tolerateTruncation: Schema.Boolean.pipe(Schema.optional, Schema.mutableKey),
}))();
export type FpackDecodeOptions = typeof FpackDecodeOptions.Type;

export const FpackDecodeResult = /* @__PURE__ */ (() => Schema.Struct({
  frames: Schema.Array(FpackFrame).pipe(Schema.mutable, Schema.mutableKey),
  frameIndex: Schema.Array(FpackFrameIndexEntry).pipe(Schema.mutable, Schema.mutableKey),
  truncated: Schema.Boolean.pipe(Schema.mutableKey),
}))();
export type FpackDecodeResult = typeof FpackDecodeResult.Type;

const requireDimension = (value: number | undefined, label: string): number =>
  schemaParser(Schema.UndefinedOr(FpackDimension),
    () => new RangeError(`${label} must be an integer between 0 and 65535.`))(value) ?? 0;

function requireFpackBytes(available: number, required: number, detail: string, tolerate: boolean): boolean {
  const decoded = Schema.decodeUnknownResult(Schema.Number.check(Schema.isGreaterThanOrEqualTo(required)))(available);
  if (Result.isFailure(decoded) && tolerate) return false;
  Result.getOrThrowWith(decoded, () => new RangeError(`Truncated fpack segment: ${detail}.`));
  return true;
}

export function encodeFpackSegment(
  frames: readonly FpackFrameInput[]
): FpackEncodeResult {
  let total = FPACK_MAGIC_BYTES.length;
  for (const frame of frames) {
    fpackJpeg(frame.jpeg);
    fpackTimestamp(frame.timestampMs);
    total += FPACK_FRAME_HEADER_BYTES + frame.jpeg.byteLength;
  }
  const bytes = new Uint8Array(total);
  const view = new DataView(bytes.buffer);
  bytes.set(FPACK_MAGIC_BYTES, 0);
  const frameIndex: FpackFrameIndexEntry[] = [];
  let offset = FPACK_MAGIC_BYTES.length;
  for (const frame of frames) {
    const width = requireDimension(frame.width, "fpack frame width");
    const height = requireDimension(frame.height, "fpack frame height");
    view.setUint32(offset, frame.jpeg.byteLength, true);
    view.setFloat64(offset + 4, frame.timestampMs, true);
    view.setUint16(offset + 12, width, true);
    view.setUint16(offset + 14, height, true);
    bytes.set(frame.jpeg, offset + FPACK_FRAME_HEADER_BYTES);
    frameIndex.push({
      timestampMs: frame.timestampMs,
      byteOffset: offset + FPACK_FRAME_HEADER_BYTES,
      byteLength: frame.jpeg.byteLength
    });
    offset += FPACK_FRAME_HEADER_BYTES + frame.jpeg.byteLength;
  }
  return { bytes, frameIndex };
}

export function decodeFpackSegment(
  bytes: Uint8Array,
  options: FpackDecodeOptions = {}
): FpackDecodeResult {
  const tolerate = options.tolerateTruncation === true;
  const available = Math.min(bytes.byteLength, FPACK_MAGIC_BYTES.length);
  for (let index = 0; index < available; index += 1) {
    schemaParser(Schema.Literal(FPACK_MAGIC_BYTES[index]),
      () => new TypeError("Not an fpack segment: bad magic."))(bytes[index]);
  }
  if (!requireFpackBytes(bytes.byteLength, FPACK_MAGIC_BYTES.length, "incomplete magic", tolerate)) {
    return { frames: [], frameIndex: [], truncated: true };
  }
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const frames: FpackFrame[] = [];
  const frameIndex: FpackFrameIndexEntry[] = [];
  let offset = FPACK_MAGIC_BYTES.length;
  while (offset < bytes.byteLength) {
    if (!requireFpackBytes(bytes.byteLength, offset + FPACK_FRAME_HEADER_BYTES, "incomplete frame header", tolerate)) {
      return { frames, frameIndex, truncated: true };
    }
    const byteLength = view.getUint32(offset, true);
    const timestampMs = view.getFloat64(offset + 4, true);
    const width = view.getUint16(offset + 12, true);
    const height = view.getUint16(offset + 14, true);
    const start = offset + FPACK_FRAME_HEADER_BYTES;
    if (!requireFpackBytes(bytes.byteLength, start + byteLength, "incomplete frame bytes", tolerate)) {
      return { frames, frameIndex, truncated: true };
    }
    frames.push({
      timestampMs,
      jpeg: bytes.slice(start, start + byteLength),
      ...(width === 0 ? {} : { width }),
      ...(height === 0 ? {} : { height })
    });
    frameIndex.push({ timestampMs, byteOffset: start, byteLength });
    offset = start + byteLength;
  }
  return { frames, frameIndex, truncated: false };
}

// ---------------------------------------------------------------------------
// rrweb JSONL segments
// ---------------------------------------------------------------------------

/** One rrweb stream line: a `[windowId, event]` pair. */
export const FlowRecordingRrwebEntry = /* @__PURE__ */ (() => Schema.Tuple([Schema.String, Schema.Unknown]).pipe(Schema.mutable))();
export type FlowRecordingRrwebEntry = typeof FlowRecordingRrwebEntry.Type;

export const RRWEB_EVENT_TYPE_FULL_SNAPSHOT = 2;

export const isRrwebFullSnapshot = /* @__PURE__ */ (() => Schema.is(Schema.Struct({
  type: Schema.Literal(RRWEB_EVENT_TYPE_FULL_SNAPSHOT)
})))();

/**
 * Segments rotate at full-snapshot checkout boundaries so every segment
 * replays independently from its own snapshot.
 */
export function shouldRotateRrwebSegment(
  event: unknown,
  entriesInSegment: number
): boolean {
  return entriesInSegment > 0 && isRrwebFullSnapshot(event);
}

/** True only for a well-formed `[windowId, event]` pair. */
export const isRrwebEntry = /* @__PURE__ */ (() => Schema.is(FlowRecordingRrwebEntry))();
const rrwebEntry = /* @__PURE__ */ (() => schemaDecoder(FlowRecordingRrwebEntry, () => new TypeError("rrweb entries must be [windowId, event] pairs.")))();
export function serializeRrwebEntry(entry: FlowRecordingRrwebEntry): string {
  return JSON.stringify(rrwebEntry.parse(entry));
}

/** JSONL: one `[windowId, event]` pair per newline-terminated line. */
export function serializeRrwebSegment(
  entries: readonly FlowRecordingRrwebEntry[]
): string {
  let output = "";
  for (const entry of entries) {
    output += `${serializeRrwebEntry(entry)}\n`;
  }
  return output;
}

export const RrwebSegmentParseOptions = /* @__PURE__ */ (() => Schema.Struct({
  /** Drop an incomplete trailing line instead of throwing. */
  tolerateTruncation: Schema.Boolean.pipe(Schema.optional, Schema.mutableKey),
}))();
export type RrwebSegmentParseOptions = typeof RrwebSegmentParseOptions.Type;

export const RrwebSegmentParseResult = /* @__PURE__ */ (() => Schema.Struct({
  entries: Schema.Array(FlowRecordingRrwebEntry).pipe(Schema.mutable, Schema.mutableKey),
  truncated: Schema.Boolean.pipe(Schema.mutableKey),
}))();
export type RrwebSegmentParseResult = typeof RrwebSegmentParseResult.Type;

const rrwebJson = /* @__PURE__ */ (() => Schema.decodeUnknownResult(Schema.fromJsonString(Schema.Unknown)))();
const rrwebLine = /* @__PURE__ */ (() => schemaDecoder(FlowRecordingRrwebEntry, () => new TypeError("rrweb segment lines must be [windowId, event] pairs.")))();

/** Decode JSONL entries while preserving the historical final-line recovery. */
export function parseRrwebSegment(
  text: string,
  options: RrwebSegmentParseOptions = {}
): RrwebSegmentParseResult {
  const entries: FlowRecordingRrwebEntry[] = [];
  const lines = text.split("\n");
  for (const [index, line] of lines.entries()) {
    if (line === "") continue;
    const final = index === lines.length - 1;
    const parsed = rrwebJson(line);
    if (Result.isFailure(parsed) && final && options.tolerateTruncation === true) return { entries, truncated: true };
    const entry = Result.getOrThrowWith(parsed, () => new SyntaxError(final
      ? "Truncated rrweb segment: incomplete final line."
      : "Corrupt rrweb segment line."));
    entries.push(rrwebLine.parse(entry));
  }
  return { entries, truncated: false };
}

async function pipeBytes(
  bytes: Uint8Array,
  transform: {
    readable: ReadableStream<Uint8Array>;
    writable: WritableStream<BufferSource>;
  }
): Promise<Uint8Array> {
  const writer = transform.writable.getWriter();
  const wrote = writer
    .write(bytes.slice())
    .then(() => writer.close());
  const chunks: Uint8Array[] = [];
  const reader = transform.readable.getReader();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
  }
  await wrote;
  let total = 0;
  for (const chunk of chunks) total += chunk.byteLength;
  const output = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    output.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return output;
}

export { gzipSegmentBytes } from "./recording/compression/service.ts";

export function gunzipSegmentBytes(bytes: Uint8Array): Promise<Uint8Array> {
  return pipeBytes(bytes, new DecompressionStream("gzip"));
}

// ---------------------------------------------------------------------------
// In-page chunk protocol
// ---------------------------------------------------------------------------

export const FLOW_RECORDING_CHUNK_VERSION = 1;
export const DEFAULT_FLOW_RECORDING_CHUNK_BYTES = 256 * 1024;

/**
 * The envelope the injected runtime emits through the page binding. Field
 * order is part of the protocol: serialized envelopes are byte-identical
 * between the in-page bootstrap and `chunkFlowRecordingPayload`.
 */
export const FlowRecordingChunkEnvelope = /* @__PURE__ */ (() => Schema.Struct({
  v: Schema.Number.pipe(Schema.mutableKey),
  seq: NonNegativeSafeInteger.pipe(Schema.mutableKey),
  idx: NonNegativeSafeInteger.pipe(Schema.mutableKey),
  total: PositiveSafeInteger.pipe(Schema.mutableKey),
  data: Schema.String.pipe(Schema.mutableKey),
}).check(Schema.makeFilter(value => value.idx < value.total)))();
export type FlowRecordingChunkEnvelope = typeof FlowRecordingChunkEnvelope.Type;

export function chunkFlowRecordingPayload(
  payload: string,
  seq: number,
  chunkBytes = DEFAULT_FLOW_RECORDING_CHUNK_BYTES
): FlowRecordingChunkEnvelope[] {
  schemaParser(Schema.String, () => new TypeError("Chunk payload must be a string."))(payload);
  schemaParser(NonNegativeSafeInteger, () => new RangeError("Chunk seq must be a non-negative safe integer."))(seq);
  schemaParser(PositiveSafeInteger, () => new RangeError("Chunk budget must be a positive integer."))(chunkBytes);
  const total = Math.max(1, Math.ceil(payload.length / chunkBytes));
  const envelopes: FlowRecordingChunkEnvelope[] = [];
  for (let idx = 0; idx < total; idx += 1) {
    envelopes.push({
      v: FLOW_RECORDING_CHUNK_VERSION,
      seq,
      idx,
      total,
      data: payload.slice(idx * chunkBytes, (idx + 1) * chunkBytes)
    });
  }
  return envelopes;
}

export const FlowRecordingChunkQuarantineReason = /* @__PURE__ */ (() => Schema.Literals(["malformed", "oversized", "conflicting", "incomplete"]))();
export type FlowRecordingChunkQuarantineReason = typeof FlowRecordingChunkQuarantineReason.Type;

export const FlowRecordingChunkQuarantineRecord = /* @__PURE__ */ (() => Schema.Struct({
  reason: FlowRecordingChunkQuarantineReason.pipe(Schema.mutableKey),
  seq: Schema.Number.pipe(Schema.optional, Schema.mutableKey),
  receivedChunks: Schema.Number.pipe(Schema.optional, Schema.mutableKey),
  expectedChunks: Schema.Number.pipe(Schema.optional, Schema.mutableKey),
  detail: Schema.String.pipe(Schema.optional, Schema.mutableKey),
}))();
export type FlowRecordingChunkQuarantineRecord = typeof FlowRecordingChunkQuarantineRecord.Type;

export const FlowRecordingChunkReassemblerOptions = /* @__PURE__ */ (() => Schema.Struct({
  /** Reject any single reassembled payload beyond this many characters. */
  maxPayloadChars: PositiveSafeInteger.pipe(Schema.optional, Schema.mutableKey),
  /** Bound concurrent partial events; the oldest is quarantined on overflow. */
  maxPendingEvents: PositiveSafeInteger.pipe(Schema.optional, Schema.mutableKey),
}))();
export type FlowRecordingChunkReassemblerOptions = typeof FlowRecordingChunkReassemblerOptions.Type;

export const DEFAULT_FLOW_RECORDING_MAX_PAYLOAD_CHARS = 8 * 1024 * 1024;
export const DEFAULT_FLOW_RECORDING_MAX_PENDING_EVENTS = 4_096;

const PendingChunkedEvent = /* @__PURE__ */ (() => Schema.Struct({
  total: Schema.Number.pipe(Schema.mutableKey),
  received: Schema.Number.pipe(Schema.mutableKey),
  receivedChars: Schema.Number.pipe(Schema.mutableKey),
  chunks: Schema.Array(Schema.Union([Schema.String, Schema.Undefined])).pipe(Schema.mutable, Schema.mutableKey),
}))();
type PendingChunkedEvent = typeof PendingChunkedEvent.Type;

const chunkObject = /* @__PURE__ */ (() => Schema.decodeUnknownOption(IdentityObjectInput))();
const isChunkVersion = /* @__PURE__ */ (() => Schema.is(Schema.Literal(FLOW_RECORDING_CHUNK_VERSION)))();
const chunkEnvelope = /* @__PURE__ */ (() => Schema.decodeUnknownOption(FlowRecordingChunkEnvelope))();
const optionalChunkSeq = /* @__PURE__ */ (() => Schema.decodeUnknownOption(Schema.Int))();
const chunkJson = /* @__PURE__ */ (() => Schema.decodeUnknownOption(Schema.fromJsonString(Schema.Unknown)))();
const reassemblerBounds = /* @__PURE__ */ (() => schemaParser(Schema.Struct({
  maxPayloadChars: PositiveSafeInteger,
  maxPendingEvents: PositiveSafeInteger
}), () => new RangeError("Reassembler bounds must be positive integers.")))();

/**
 * Reassembles chunk envelopes emitted by the in-page runtime. Malformed,
 * oversized, or conflicting events are quarantined immediately; events still
 * incomplete when a segment closes are quarantined by `flush()` so a lost
 * chunk can never corrupt neighbouring events in the stream.
 */
export class FlowRecordingChunkReassembler {
  private readonly maxPayloadChars: number;
  private readonly maxPendingEvents: number;
  private readonly pending = new Map<number, PendingChunkedEvent>();
  private readonly poisoned = new Set<number>();
  private records: FlowRecordingChunkQuarantineRecord[] = [];

  constructor(options: FlowRecordingChunkReassemblerOptions = {}) {
    this.maxPayloadChars =
      options.maxPayloadChars ?? DEFAULT_FLOW_RECORDING_MAX_PAYLOAD_CHARS;
    this.maxPendingEvents =
      options.maxPendingEvents ?? DEFAULT_FLOW_RECORDING_MAX_PENDING_EVENTS;
    reassemblerBounds({ maxPayloadChars: this.maxPayloadChars, maxPendingEvents: this.maxPendingEvents });
  }

  get pendingCount(): number {
    return this.pending.size;
  }

  get quarantined(): readonly FlowRecordingChunkQuarantineRecord[] {
    return this.records;
  }

  private quarantine(record: FlowRecordingChunkQuarantineRecord): undefined {
    this.records.push(record);
    if (record.seq !== undefined) {
      this.pending.delete(record.seq);
      this.poisoned.add(record.seq);
    }
    return undefined;
  }

  /**
   * Feed one envelope (raw JSON string or parsed object). Returns the
   * complete payload when the final chunk of an event arrives.
   */
  push(raw: unknown): string | undefined {
    let envelope: unknown = raw;
    if (isString(raw)) {
      const parsed = chunkJson(raw);
      if (Option.isNone(parsed)) {
        return this.quarantine({ reason: "malformed", detail: "unparseable chunk" });
      }
      envelope = parsed.value;
    }
    const object = Option.getOrUndefined(chunkObject(envelope));
    if (!object) {
      return this.quarantine({ reason: "malformed", detail: "chunk is not an object" });
    }
    // Capture the same five fields before validating version or coordinates.
    // The envelope may have arrived as a parsed object with inherited fields.
    const fields = {
      v: Reflect.get(object, "v"),
      seq: Reflect.get(object, "seq"),
      idx: Reflect.get(object, "idx"),
      total: Reflect.get(object, "total"),
      data: Reflect.get(object, "data")
    };
    if (!isChunkVersion(fields.v)) {
      return this.quarantine({ reason: "malformed", detail: "unsupported chunk version" });
    }
    const decoded = chunkEnvelope(fields);
    if (Option.isNone(decoded)) {
      return this.quarantine({
        reason: "malformed",
        seq: Option.getOrUndefined(optionalChunkSeq(fields.seq)),
        detail: "invalid chunk envelope"
      });
    }
    const { seq: eventSeq, total: eventTotal, idx: chunkIdx, data } = decoded.value;
    if (this.poisoned.has(eventSeq)) return undefined;
    if (data.length > this.maxPayloadChars) {
      return this.quarantine({
        reason: "oversized",
        seq: eventSeq,
        expectedChunks: eventTotal,
        detail: "single chunk exceeds payload budget"
      });
    }

    let entry = this.pending.get(eventSeq);
    if (!entry) {
      if (this.pending.size >= this.maxPendingEvents) {
        const oldest = this.pending.entries().next();
        if (!oldest.done) {
          const [seq, evicted] = oldest.value;
          this.quarantine({
            reason: "incomplete",
            seq,
            receivedChunks: evicted.received,
            expectedChunks: evicted.total,
            detail: "evicted by pending-event bound"
          });
        }
      }
      entry = {
        total: eventTotal,
        received: 0,
        receivedChars: 0,
        chunks: new Array<string | undefined>(eventTotal)
      };
      this.pending.set(eventSeq, entry);
    }
    if (entry.total !== eventTotal) {
      return this.quarantine({
        reason: "conflicting",
        seq: eventSeq,
        receivedChunks: entry.received,
        expectedChunks: entry.total,
        detail: "chunk total changed mid-event"
      });
    }
    const existing = entry.chunks[chunkIdx];
    if (existing !== undefined) {
      if (existing === data) return undefined;
      return this.quarantine({
        reason: "conflicting",
        seq: eventSeq,
        receivedChunks: entry.received,
        expectedChunks: entry.total,
        detail: "duplicate chunk index with different data"
      });
    }
    entry.chunks[chunkIdx] = data;
    entry.received += 1;
    entry.receivedChars += data.length;
    if (entry.receivedChars > this.maxPayloadChars) {
      return this.quarantine({
        reason: "oversized",
        seq: eventSeq,
        receivedChunks: entry.received,
        expectedChunks: entry.total,
        detail: "reassembled payload exceeds budget"
      });
    }
    if (entry.received < entry.total) return undefined;
    this.pending.delete(eventSeq);
    return entry.chunks.join("");
  }

  /**
   * Close a segment boundary: quarantine every still-incomplete event and
   * drain the accumulated quarantine records for persistence alongside the
   * segment. Subsequent complete events continue unaffected.
   */
  flush(): FlowRecordingChunkQuarantineRecord[] {
    for (const [seq, entry] of this.pending) {
      this.records.push({
        reason: "incomplete",
        seq,
        receivedChunks: entry.received,
        expectedChunks: entry.total,
        detail: "incomplete at segment boundary"
      });
    }
    this.pending.clear();
    this.poisoned.clear();
    const drained = this.records;
    this.records = [];
    return drained;
  }
}

// ---------------------------------------------------------------------------
// Capture backpressure state machine
// ---------------------------------------------------------------------------

export const FlowRecordingCapturePumpConfig = /* @__PURE__ */ (() => Schema.Struct({
  softFlushBytes: PositiveFinite.pipe(Schema.optional, Schema.mutableKey),
  softFlushIntervalMs: PositiveFinite.pipe(Schema.optional, Schema.mutableKey),
  hardDropBytes: PositiveFinite.pipe(Schema.optional, Schema.mutableKey),
  maxDurationMs: PositiveFinite.pipe(Schema.optional, Schema.mutableKey),
  maxTotalBytes: PositiveFinite.pipe(Schema.optional, Schema.mutableKey),
  quality: PositiveFinite.pipe(Schema.optional, Schema.mutableKey),
  everyNthFrame: PositiveFinite.pipe(Schema.optional, Schema.mutableKey),
  minQuality: PositiveFinite.pipe(Schema.optional, Schema.mutableKey),
  qualityStep: PositiveFinite.pipe(Schema.optional, Schema.mutableKey),
  degradeOverflowCount: PositiveFinite.pipe(Schema.optional, Schema.mutableKey),
  degradeWindowMs: PositiveFinite.pipe(Schema.optional, Schema.mutableKey),
}))();
export type FlowRecordingCapturePumpConfig = typeof FlowRecordingCapturePumpConfig.Type;

export const FlowRecordingCapturePumpEvent = /* @__PURE__ */ (() => Schema.Union([Schema.Struct({
  type: Schema.Literal("frame").pipe(Schema.mutableKey),
  timestampMs: Schema.Number.pipe(Schema.mutableKey),
  byteLength: Schema.Number.pipe(Schema.mutableKey),
}), Schema.Struct({
  type: Schema.Literal("tick").pipe(Schema.mutableKey),
  timestampMs: Schema.Number.pipe(Schema.mutableKey),
}), Schema.Struct({
  type: Schema.Literal("flushed").pipe(Schema.mutableKey),
  timestampMs: Schema.Number.pipe(Schema.mutableKey),
  bytes: Schema.Number.pipe(Schema.mutableKey),
  frames: Schema.Number.pipe(Schema.mutableKey),
}), Schema.Struct({
  type: Schema.Literal("stop").pipe(Schema.mutableKey),
  timestampMs: Schema.Number.pipe(Schema.mutableKey),
})]))();
export type FlowRecordingCapturePumpEvent = typeof FlowRecordingCapturePumpEvent.Type;

export const FlowRecordingMarkerIntent = /* @__PURE__ */ (() => Schema.Struct({
  kind: Schema.Literals(["dropped-frames", "quality-degraded", "truncated"]).pipe(Schema.mutableKey),
  label: Schema.String.pipe(Schema.mutableKey),
  timestampMs: Schema.Number.pipe(Schema.mutableKey),
  detail: Schema.Record(Schema.String, Schema.mutableKey(Schema.Union([Schema.Number, Schema.String]))).pipe(Schema.mutableKey),
}))();
export type FlowRecordingMarkerIntent = typeof FlowRecordingMarkerIntent.Type;

export const FlowRecordingCapturePumpIntent = /* @__PURE__ */ (() => Schema.Union([Schema.Struct({
  type: Schema.Literal("flush").pipe(Schema.mutableKey),
  reason: Schema.Literals(["soft-bytes", "soft-interval", "final"]).pipe(Schema.mutableKey),
}), Schema.Struct({
  type: Schema.Literal("marker").pipe(Schema.mutableKey),
  marker: FlowRecordingMarkerIntent.pipe(Schema.mutableKey),
}), Schema.Struct({
  type: Schema.Literal("restart-screencast").pipe(Schema.mutableKey),
  quality: Schema.Number.pipe(Schema.mutableKey),
  everyNthFrame: Schema.Number.pipe(Schema.mutableKey),
}), Schema.Struct({
  type: Schema.Literal("stop").pipe(Schema.mutableKey),
  reason: Schema.Literals(["duration-cap", "byte-cap"]).pipe(Schema.mutableKey),
})]))();
export type FlowRecordingCapturePumpIntent = typeof FlowRecordingCapturePumpIntent.Type;

export const FlowRecordingCapturePumpState = /* @__PURE__ */ (() => Schema.Struct({
  startedAtMs: Schema.Number,
  stopped: Schema.Boolean,
  stopReason: Schema.Literals(["duration-cap", "byte-cap", "stopped"]).pipe(Schema.optional),
  bufferedBytes: Schema.Number,
  bufferedFrames: Schema.Number,
  acceptedBytes: Schema.Number,
  acceptedFrames: Schema.Number,
  flushInFlight: Schema.Boolean,
  lastFlushAtMs: Schema.Number,
  dropGapStartMs: Schema.Number.pipe(Schema.optional),
  droppedInGap: Schema.Number,
  totalDroppedFrames: Schema.Number,
  quality: Schema.Number,
  everyNthFrame: Schema.Number,
  degradeSteps: Schema.Number,
  overflowTimestamps: Schema.Array(Schema.Number),
}))();
export type FlowRecordingCapturePumpState = typeof FlowRecordingCapturePumpState.Type;

export const FlowRecordingCapturePumpResult = /* @__PURE__ */ (() => Schema.Struct({
  state: FlowRecordingCapturePumpState.pipe(Schema.mutableKey),
  intents: Schema.Array(FlowRecordingCapturePumpIntent).pipe(Schema.mutable, Schema.mutableKey),
}))();
export type FlowRecordingCapturePumpResult = typeof FlowRecordingCapturePumpResult.Type;

export interface FlowRecordingCapturePump {
  config: Required<FlowRecordingCapturePumpConfig>;
  initialState(nowMs: number): FlowRecordingCapturePumpState;
  reduce(
    state: FlowRecordingCapturePumpState,
    event: FlowRecordingCapturePumpEvent
  ): FlowRecordingCapturePumpResult;
}

/**
 * Pure reducer for the screencast capture pump. The ack-first invariant is
 * structural: this machine has no acknowledgement event or intent — callers
 * MUST acknowledge every screencast frame unconditionally before feeding a
 * `frame` event here, so no state below can ever gate or delay an ack.
 */
export function createCapturePump(
  config: FlowRecordingCapturePumpConfig = {}
): FlowRecordingCapturePump {
  const resolved: Required<FlowRecordingCapturePumpConfig> = {
    softFlushBytes:
      config.softFlushBytes ?? FLOW_RECORDING_DEFAULT_POLICY.softFlushBytes,
    softFlushIntervalMs:
      config.softFlushIntervalMs ??
      FLOW_RECORDING_DEFAULT_POLICY.softFlushIntervalMs,
    hardDropBytes:
      config.hardDropBytes ?? FLOW_RECORDING_DEFAULT_POLICY.hardDropBytes,
    maxDurationMs:
      config.maxDurationMs ?? FLOW_RECORDING_DEFAULT_POLICY.maxDurationMs,
    maxTotalBytes:
      config.maxTotalBytes ?? FLOW_RECORDING_DEFAULT_POLICY.maxTotalBytes,
    quality: config.quality ?? FLOW_RECORDING_DEFAULT_POLICY.screencast.quality,
    everyNthFrame:
      config.everyNthFrame ??
      FLOW_RECORDING_DEFAULT_POLICY.screencast.everyNthFrame,
    minQuality: config.minQuality ?? FLOW_RECORDING_DEFAULT_POLICY.minQuality,
    qualityStep: config.qualityStep ?? 10,
    degradeOverflowCount: config.degradeOverflowCount ?? 3,
    degradeWindowMs: config.degradeWindowMs ?? 10_000
  };
  for (const [label, value] of Object.entries(resolved)) {
    schemaParser(PositiveFinite,
      () => new RangeError(`Capture pump ${label} must be a positive number.`))(value);
  }
  schemaParser(Schema.Struct({ softFlushBytes: PositiveFinite, hardDropBytes: PositiveFinite }).check(
    Schema.makeFilter(value => value.softFlushBytes <= value.hardDropBytes)
  ), () => new RangeError("Capture pump soft flush threshold must not exceed the hard drop threshold."))(resolved);

  function closeDropGap(
    state: FlowRecordingCapturePumpState,
    timestampMs: number,
    intents: FlowRecordingCapturePumpIntent[]
  ): FlowRecordingCapturePumpState {
    if (state.dropGapStartMs === undefined) return state;
    intents.push({
      type: "marker",
      marker: {
        kind: "dropped-frames",
        label: `Dropped ${state.droppedInGap} frames under backpressure`,
        timestampMs,
        detail: {
          droppedFrames: state.droppedInGap,
          fromMs: state.dropGapStartMs,
          toMs: timestampMs
        }
      }
    });
    return {
      ...state,
      dropGapStartMs: undefined,
      droppedInGap: 0,
      totalDroppedFrames: state.totalDroppedFrames + state.droppedInGap
    };
  }

  function truncateStop(
    state: FlowRecordingCapturePumpState,
    timestampMs: number,
    reason: "duration-cap" | "byte-cap"
  ): FlowRecordingCapturePumpResult {
    const intents: FlowRecordingCapturePumpIntent[] = [];
    let next = closeDropGap(state, timestampMs, intents);
    intents.push({
      type: "marker",
      marker: {
        kind: "truncated",
        label:
          reason === "duration-cap"
            ? "Recording truncated at its duration cap"
            : "Recording truncated at its byte cap",
        timestampMs,
        detail: {
          reason,
          maxDurationMs: resolved.maxDurationMs,
          maxTotalBytes: resolved.maxTotalBytes
        }
      }
    });
    if (next.bufferedFrames > 0) {
      intents.push({ type: "flush", reason: "final" });
    }
    intents.push({ type: "stop", reason });
    next = { ...next, stopped: true, stopReason: reason };
    return { state: next, intents };
  }

  return {
    config: resolved,
    initialState(nowMs: number): FlowRecordingCapturePumpState {
      return {
        startedAtMs: nowMs,
        stopped: false,
        bufferedBytes: 0,
        bufferedFrames: 0,
        acceptedBytes: 0,
        acceptedFrames: 0,
        flushInFlight: false,
        lastFlushAtMs: nowMs,
        dropGapStartMs: undefined,
        droppedInGap: 0,
        totalDroppedFrames: 0,
        quality: resolved.quality,
        everyNthFrame: resolved.everyNthFrame,
        degradeSteps: 0,
        overflowTimestamps: []
      };
    },
    reduce(state, event): FlowRecordingCapturePumpResult {
      if (state.stopped) return { state, intents: [] };
      const intents: FlowRecordingCapturePumpIntent[] = [];

      if (event.type === "stop") {
        let next = closeDropGap(state, event.timestampMs, intents);
        if (next.bufferedFrames > 0) {
          intents.push({ type: "flush", reason: "final" });
        }
        next = { ...next, stopped: true, stopReason: "stopped" };
        return { state: next, intents };
      }

      if (event.type === "flushed") {
        let next: FlowRecordingCapturePumpState = {
          ...state,
          bufferedBytes: Math.max(0, state.bufferedBytes - event.bytes),
          bufferedFrames: Math.max(0, state.bufferedFrames - event.frames),
          flushInFlight: false,
          lastFlushAtMs: event.timestampMs
        };
        if (next.bufferedBytes >= resolved.softFlushBytes) {
          intents.push({ type: "flush", reason: "soft-bytes" });
          next = { ...next, flushInFlight: true };
        }
        return { state: next, intents };
      }

      if (event.type === "tick") {
        if (event.timestampMs - state.startedAtMs >= resolved.maxDurationMs) {
          return truncateStop(state, event.timestampMs, "duration-cap");
        }
        if (
          !state.flushInFlight &&
          state.bufferedFrames > 0 &&
          event.timestampMs - state.lastFlushAtMs >= resolved.softFlushIntervalMs
        ) {
          intents.push({ type: "flush", reason: "soft-interval" });
          return {
            state: { ...state, flushInFlight: true },
            intents
          };
        }
        return { state, intents };
      }

      // event.type === "frame": the caller has already acked this frame.
      if (event.timestampMs - state.startedAtMs >= resolved.maxDurationMs) {
        return truncateStop(state, event.timestampMs, "duration-cap");
      }
      if (event.byteLength < 0 || !Number.isFinite(event.byteLength)) {
        return { state, intents };
      }
      if (state.acceptedBytes + event.byteLength > resolved.maxTotalBytes) {
        return truncateStop(state, event.timestampMs, "byte-cap");
      }
      if (state.bufferedBytes + event.byteLength > resolved.hardDropBytes) {
        const overflowTimestamps = [
          ...state.overflowTimestamps.filter(
            (at) => event.timestampMs - at < resolved.degradeWindowMs
          ),
          event.timestampMs
        ];
        let next: FlowRecordingCapturePumpState = {
          ...state,
          dropGapStartMs: state.dropGapStartMs ?? event.timestampMs,
          droppedInGap: state.droppedInGap + 1,
          overflowTimestamps
        };
        if (overflowTimestamps.length >= resolved.degradeOverflowCount) {
          const quality = Math.max(
            resolved.minQuality,
            next.quality - resolved.qualityStep
          );
          const everyNthFrame = next.everyNthFrame * 2;
          next = {
            ...next,
            quality,
            everyNthFrame,
            degradeSteps: next.degradeSteps + 1,
            overflowTimestamps: []
          };
          intents.push({
            type: "restart-screencast",
            quality,
            everyNthFrame
          });
          intents.push({
            type: "marker",
            marker: {
              kind: "quality-degraded",
              label: `Capture degraded to quality ${quality}, every ${everyNthFrame}th frame`,
              timestampMs: event.timestampMs,
              detail: {
                quality,
                everyNthFrame,
                degradeSteps: next.degradeSteps
              }
            }
          });
        }
        return { state: next, intents };
      }

      let next = closeDropGap(state, event.timestampMs, intents);
      next = {
        ...next,
        bufferedBytes: next.bufferedBytes + event.byteLength,
        bufferedFrames: next.bufferedFrames + 1,
        acceptedBytes: next.acceptedBytes + event.byteLength,
        acceptedFrames: next.acceptedFrames + 1
      };
      if (
        !next.flushInFlight &&
        next.bufferedBytes >= resolved.softFlushBytes
      ) {
        intents.push({ type: "flush", reason: "soft-bytes" });
        next = { ...next, flushInFlight: true };
      }
      return { state: next, intents };
    }
  };
}

// ---------------------------------------------------------------------------
// Marker normalization and clock calibration
// ---------------------------------------------------------------------------

export const FLOW_RECORDING_MARKER_LABEL_LIMIT = 500;
export const FLOW_RECORDING_MARKER_DETAIL_LIMIT = 4_000;

export const FlowRecordingMarkerInput = /* @__PURE__ */ (() => Schema.Struct({
  kind: Schema.Unknown.pipe(Schema.mutableKey),
  label: Schema.Unknown.pipe(Schema.optional, Schema.mutableKey),
  timestampMs: Schema.Unknown.pipe(Schema.mutableKey),
  detail: Schema.Unknown.pipe(Schema.optional, Schema.mutableKey),
}))();
export type FlowRecordingMarkerInput = typeof FlowRecordingMarkerInput.Type;

/**
 * Normalize an untrusted marker: invalid kinds and timestamps reject the
 * marker; labels are capped; details survive only when JSON-serializable
 * within the detail budget.
 */
const MarkerIdentity = /* @__PURE__ */ (() => Schema.Struct({
  kind: FlowRecordingMarkerKind,
  timestampMs: Schema.Finite.check(Schema.isGreaterThanOrEqualTo(0))
}))();
const markerIdentity = /* @__PURE__ */ (() => Schema.decodeUnknownOption(MarkerIdentity))();
export function normalizeFlowRecordingMarker(
  input: FlowRecordingMarkerInput
): FlowRecordingMarker | undefined {
  const decoded = markerIdentity(input);
  if (Option.isNone(decoded)) return undefined;
  const identity = decoded.value;
  const rawLabel =
    isString(input.label) && input.label.trim() !== ""
      ? input.label.trim()
      : identity.kind;
  const label = rawLabel.slice(0, FLOW_RECORDING_MARKER_LABEL_LIMIT);
  let detail: unknown;
  if (input.detail !== undefined) {
    try {
      const json = JSON.stringify(input.detail);
      if (json === undefined) {
        detail = undefined;
      } else if (json.length > FLOW_RECORDING_MARKER_DETAIL_LIMIT) {
        detail = { truncated: true, originalCharacters: json.length };
      } else {
        detail = JSON.parse(json);
      }
    } catch {
      detail = undefined;
    }
  }
  return {
    kind: identity.kind,
    label,
    timestampMs: Math.floor(identity.timestampMs),
    ...(detail === undefined ? {} : { detail })
  };
}

/**
 * Host-minus-page clock offset, calibrated once per recording from a
 * `Runtime.evaluate(Date.now())` round-trip.
 */
export function computeClockOffset(
  pageNowMs: number,
  hostNowMs: number
): number {
  schemaParser(Schema.Tuple([Schema.Finite, Schema.Finite]),
    () => new RangeError("Clock calibration requires finite timestamps."))([pageNowMs, hostNowMs]);
  return hostNowMs - pageNowMs;
}

/** Align a page-clock timestamp onto the host timeline. */
export function alignPageTimestamp(
  timestampMs: number,
  offsetMs: number
): number {
  schemaParser(Schema.Tuple([Schema.Finite, Schema.Finite]),
    () => new RangeError("Clock alignment requires finite timestamps."))([timestampMs, offsetMs]);
  return timestampMs + offsetMs;
}

// ---------------------------------------------------------------------------
// Default policy
// ---------------------------------------------------------------------------

export const FLOW_RECORDING_RETENTION_MS = 7 * 24 * 60 * 60 * 1_000;

export const FLOW_RECORDING_DEFAULT_POLICY = {
  screencast: {
    format: "jpeg",
    quality: 60,
    maxWidth: 1280,
    maxHeight: 720,
    // Capture every composited frame (not every other) so animations/spinners
    // replay fluidly rather than stepping.
    everyNthFrame: 1
  },
  checkoutEveryNms: 60_000,
  blockClass: "ws-no-record",
  maskAllInputs: true,
  maskPasswords: true,
  recordCanvas: false,
  canvasSampleFps: 2,
  canvasQuality: 0.4,
  retentionMs: FLOW_RECORDING_RETENTION_MS,
  chunkBytes: DEFAULT_FLOW_RECORDING_CHUNK_BYTES,
  softFlushBytes: 6 * 1024 * 1024,
  softFlushIntervalMs: 5_000,
  hardDropBytes: 12 * 1024 * 1024,
  minQuality: 30,
  maxDurationMs: 15 * 60 * 1_000,
  maxTotalBytes: 256 * 1024 * 1024
} as const satisfies {
  screencast: FlowRecordingScreencastPolicy;
  [key: string]: unknown;
};

/** The applied-policy snapshot for a recording using every default. */
export function defaultFlowRecordingAppliedPolicy(): FlowRecordingAppliedPolicy {
  return {
    maskAllInputs: FLOW_RECORDING_DEFAULT_POLICY.maskAllInputs,
    maskPasswords: FLOW_RECORDING_DEFAULT_POLICY.maskPasswords,
    blockClass: FLOW_RECORDING_DEFAULT_POLICY.blockClass,
    recordCanvas: FLOW_RECORDING_DEFAULT_POLICY.recordCanvas,
    checkoutEveryNms: FLOW_RECORDING_DEFAULT_POLICY.checkoutEveryNms,
    screencast: { ...FLOW_RECORDING_DEFAULT_POLICY.screencast },
    retentionMs: FLOW_RECORDING_DEFAULT_POLICY.retentionMs,
    maxDurationMs: FLOW_RECORDING_DEFAULT_POLICY.maxDurationMs,
    maxTotalBytes: FLOW_RECORDING_DEFAULT_POLICY.maxTotalBytes
  };
}

export const FlowRecordingSummary = /* @__PURE__ */ (() => Schema.Struct({
  recordingId: Schema.String.pipe(Schema.mutableKey),
  workspaceId: Schema.String.pipe(Schema.mutableKey),
  stationId: Schema.String.pipe(Schema.optional, Schema.mutableKey),
  sessionId: Schema.String.pipe(Schema.optional, Schema.mutableKey),
  changeId: Schema.String.pipe(Schema.optional, Schema.mutableKey),
  taskId: Schema.String.pipe(Schema.optional, Schema.mutableKey),
  runId: Schema.String.pipe(Schema.optional, Schema.mutableKey),
  attemptId: Schema.String.pipe(Schema.optional, Schema.mutableKey),
  flowName: Schema.String.pipe(Schema.mutableKey),
  mode: FlowRecordingMode.pipe(Schema.mutableKey),
  source: Schema.String.pipe(Schema.mutableKey),
  state: FlowRecordingState.pipe(Schema.mutableKey),
  startedAt: Schema.Number.pipe(Schema.mutableKey),
  endedAt: Schema.Number.pipe(Schema.optional, Schema.mutableKey),
  heartbeatAt: Schema.Number.pipe(Schema.optional, Schema.mutableKey),
  durationMs: Schema.Number.pipe(Schema.optional, Schema.mutableKey),
  frameCount: Schema.Number.pipe(Schema.mutableKey),
  eventCount: Schema.Number.pipe(Schema.mutableKey),
  markerCount: Schema.Number.pipe(Schema.mutableKey),
  segmentCount: Schema.Number.pipe(Schema.mutableKey),
  totalBytes: Schema.Number.pipe(Schema.mutableKey),
  appliedPolicy: FlowRecordingAppliedPolicy.pipe(Schema.mutableKey),
  runtimeVersion: Schema.Number.pipe(Schema.optional, Schema.mutableKey),
  exportStatus: Schema.String.pipe(Schema.optional, Schema.mutableKey),
  expiresAt: Schema.Number.pipe(Schema.mutableKey),
}))();
export type FlowRecordingSummary = typeof FlowRecordingSummary.Type;

export const FlowRecordingSegmentSummary = /* @__PURE__ */ (() => Schema.Struct({
  recordingId: Schema.String.pipe(Schema.mutableKey),
  seq: Schema.Number.pipe(Schema.mutableKey),
  kind: FlowRecordingSegmentKind.pipe(Schema.mutableKey),
  objectKey: Schema.String.pipe(Schema.mutableKey),
  firstTs: Schema.Number.pipe(Schema.mutableKey),
  lastTs: Schema.Number.pipe(Schema.mutableKey),
  itemCount: Schema.Number.pipe(Schema.mutableKey),
  byteSize: Schema.Number.pipe(Schema.mutableKey),
  frameIndex: Schema.Unknown.pipe(Schema.optional, Schema.mutableKey),
}))();
export type FlowRecordingSegmentSummary = typeof FlowRecordingSegmentSummary.Type;

export const FlowRecordingManifestResult = /* @__PURE__ */ (() => Schema.Struct({
  recording: FlowRecordingSummary.pipe(Schema.mutableKey),
  segments: Schema.Array(FlowRecordingSegmentSummary).pipe(Schema.mutable, Schema.mutableKey),
  markers: Schema.Array(Schema.Struct({
    ...FlowRecordingMarker.fields, ...Schema.Struct({
      markerSeq: Schema.Number.pipe(Schema.mutableKey),
    }).fields
  })).pipe(Schema.mutable, Schema.mutableKey),
  generatedAt: Schema.Number.pipe(Schema.mutableKey),
}))();
export type FlowRecordingManifestResult = typeof FlowRecordingManifestResult.Type;

export const FlowRecordingListResult = /* @__PURE__ */ (() => Schema.Struct({
  recordings: Schema.Array(FlowRecordingSummary).pipe(Schema.mutable, Schema.mutableKey),
  stats: Schema.Struct({
    total: Schema.Number.pipe(Schema.mutableKey),
    byState: Schema.Record(Schema.String, Schema.mutableKey(Schema.Number)).pipe(Schema.mutableKey),
    byMode: Schema.Record(Schema.String, Schema.mutableKey(Schema.Number)).pipe(Schema.mutableKey),
    totalBytes: Schema.Number.pipe(Schema.mutableKey),
  }).pipe(Schema.mutableKey),
  policy: Schema.Struct({
    retentionMs: Schema.Number.pipe(Schema.mutableKey),
    workspaceRecordingLimit: Schema.Number.pipe(Schema.mutableKey),
    workspaceByteLimit: Schema.Number.pipe(Schema.mutableKey),
  }).pipe(Schema.mutableKey),
  generatedAt: Schema.Number.pipe(Schema.mutableKey),
}))();
export type FlowRecordingListResult = typeof FlowRecordingListResult.Type;
